import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, videos } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'recommended');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredVideos = useMemo(() => {
    if (activeSlug === 'recommended') return videos;
    return videos.filter((v) => v.category === activeSlug);
  }, [activeSlug]);

  useEffect(() => {
    document.title = `${currentCategory?.name || '分类'} - 欲火视频高清资源在线观看`;
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${window.location.origin}/category/${activeSlug}`);
  }, [currentCategory, activeSlug]);

  return (
    <main>
      <Breadcrumb
        items={[
          { name: currentCategory?.name || '分类' },
        ]}
      />

      <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-16">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
            {currentCategory?.name || '全部分类'}
          </h1>
          {currentCategory && (
            <p className="mt-2 text-sm text-foreground-600">
              {currentCategory.description} — 欲火视频为您精选优质内容
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-nowrap scrollbar-hide">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              onClick={() => setActiveSlug(cat.slug)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
            {filteredVideos.map((video) => (
              <VideoCard
                key={video.id}
                id={video.id}
                title={video.title}
                category={video.category}
                thumbnail={video.thumbnail}
                duration={video.duration}
                views={video.views}
                rating={video.rating}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200">
              <i className="ri-video-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600">该分类暂无内容，请查看其他分类</p>
          </div>
        )}
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '分类'} - 欲火视频`,
            description: currentCategory?.description || '欲火视频分类浏览',
            url: `${window.location.origin}/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '欲火视频',
              url: window.location.origin,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin + '/' },
                { '@type': 'ListItem', position: 2, name: currentCategory?.name || '分类' },
              ],
            },
          }),
        }}
      />
    </main>
  );
}